import { ActionTree } from 'vuex';
import { IUpdateVideoPayload } from '../types/update-video-payload';
import { IEngagementVideoState } from './engagement-video.types';
import {
  EEngagementVideoAction,
  EEngagementVideoMutation,
  getDefaultState,
} from './engagement-video.constants';
import { RootState } from '@/store/store';
import { VideoService } from '@/api/video/services/video.service';
import { IVideo } from '@/types/Video';
import { translate } from '@/helpers/translation.helper';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import { IEngagementVideo } from '@lib-shared/types/engagement-video';
import bugsnagClient from '@lib-shared/bugsnag';
import { EVideoSource } from '@/video/types/enums/video-source';

export const actions: ActionTree<IEngagementVideoState, RootState> = {
  [EEngagementVideoAction.Reset]({ commit }) {
    commit(EEngagementVideoMutation.SetState, getDefaultState());
  },
  async [EEngagementVideoAction.Add](
    { commit, dispatch },
    { documentId, videoId }: { documentId: string; videoId: string }
  ) {
    commit(EEngagementVideoMutation.SetAdding, true);
    try {
      const success: boolean = await VideoService.addEngageVideo(videoId, documentId);
      if (!success) {
        dispatch(EEngagementVideoAction.AddFailed);
        return;
      }
      const video: IVideo = await VideoService.loadEngageVideo(documentId);
      const engagementVideo: IEngagementVideo = {
        id: video.id,
        video,
      };
      dispatch(EEngagementVideoAction.AddSuccess, engagementVideo);
    } catch (e) {
      dispatch(EEngagementVideoAction.AddFailed);
    }
  },
  [EEngagementVideoAction.AddFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-add-engagement-video').toString(),
      {
        root: true,
      }
    );
  },
  [EEngagementVideoAction.AddSuccess]({ commit }, video: IEngagementVideo) {
    commit(EEngagementVideoMutation.SetVideo, video);
    commit(EEngagementVideoMutation.SetAdding, false);
  },
  async [EEngagementVideoAction.Load]({ dispatch, commit }) {
    commit(EEngagementVideoMutation.SetLoading, true);
    try {
      const videos: IVideo[] = await VideoService.loadEngageVideos();
      const filterdVideos: IVideo[] = videos.filter(
        (video: IVideo, index: number, array: IVideo[]) =>
          array.findIndex(t => t.videoUrl == video.videoUrl) == index
      );
      dispatch(EEngagementVideoAction.LoadSuccess, filterdVideos);
    } catch (error) {
      dispatch(EEngagementVideoAction.LoadFailed, error);
    }
  },
  [EEngagementVideoAction.LoadFailed]({ commit, dispatch }, error: string) {
    if (bugsnagClient) {
      bugsnagClient.notify(error);
    }
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-load-engagement-videos').toString(),
      {
        root: true,
      }
    );
    commit(EEngagementVideoMutation.SetLoading, false);
    commit(EEngagementVideoMutation.SetLoaded, false);
  },
  [EEngagementVideoAction.LoadSuccess]({ commit }, videos: IVideo[]) {
    commit(EEngagementVideoMutation.SetVideos, videos);
    commit(EEngagementVideoMutation.SetLoading, false);
    commit(EEngagementVideoMutation.SetLoaded, true);
  },
  async [EEngagementVideoAction.Remove]({ state, dispatch, commit }, videoId: string) {
    const originalVideos: IVideo[] = state.videos;
    commit(EEngagementVideoMutation.Remove, videoId);
    try {
      await VideoService.deleteEngageVideo(videoId);
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      dispatch(EEngagementVideoAction.RemoveFailed, originalVideos);
    }
  },
  [EEngagementVideoAction.RemoveFailed]({ dispatch, commit }, videos: IVideo[]) {
    commit(EEngagementVideoMutation.SetVideos, videos);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-delete-engagement-video').toString(),
      {
        root: true,
      }
    );
  },
  async [EEngagementVideoAction.UpdateVideo](
    { state, dispatch, commit },
    payload: IUpdateVideoPayload
  ) {
    const originalVideos: IVideo[] = state.videos;
    try {
      commit(EEngagementVideoMutation.SetLoading, true);
      const updatedVideos: IVideo[] = state.videos.map((video: IVideo) =>
        video.videoId === payload.videoId
          ? { ...video, videoTitle: payload.videoTitle, published: payload.published }
          : video
      );
      commit(EEngagementVideoMutation.SetVideos, updatedVideos);
      await VideoService.updateVideo(payload);
      commit(EEngagementVideoMutation.SetLoading, false);
    } catch {
      dispatch(EEngagementVideoAction.UpdateVideoFailed, originalVideos);
    }
  },
  [EEngagementVideoAction.UpdateVideoFailed]({ dispatch, commit }, originalVideos: IVideo[]) {
    commit(EEngagementVideoMutation.SetVideos, originalVideos);
    commit(EEngagementVideoMutation.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-update-engagement-video').toString(),
      {
        root: true,
      }
    );
  },
  async [EEngagementVideoAction.UploadVideoFromUrl](
    { dispatch },
    {
      videoTitle,
      published,
      saved,
      videoUrl,
      videoType,
      documentId,
    }: {
      videoTitle: string;
      published: boolean;
      saved: boolean;
      videoUrl: string;
      videoType: EVideoSource;
      documentId: string;
    }
  ) {
    try {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowInfo}`,
        translate('started-to-upload-your-engagement-video').toString(),
        {
          root: true,
        }
      );
      const { video } = await VideoService.createVideo({
        videoTitle,
        published,
        saved,
        videoUrl,
        videoType,
      });
      await VideoService.addEngageVideo(video.id, documentId);
      dispatch(EEngagementVideoAction.UploadVideoSuccess, video);
    } catch (error) {
      dispatch(EEngagementVideoAction.UploadVideoFailed, error);
    }
  },

  [EEngagementVideoAction.UploadVideoSuccess]({ dispatch }, video: IVideo) {
    const engageVideo: IEngagementVideo = {
      id: video.id,
      video,
    };
    dispatch('documentEdit/assembler/addEngageVideo', engageVideo, {
      root: true,
    });
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate('uploaded-video-successfully').toString(),
      {
        root: true,
      }
    );
  },

  [EEngagementVideoAction.UploadVideoFailed]({ dispatch }, error: string) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-upload-engagement-video').toString(),
      {
        root: true,
      }
    );
    if (bugsnagClient) {
      bugsnagClient.notify(error);
    }
  },

  async [EEngagementVideoAction.UploadVideoFromFile](
    { dispatch },
    {
      file,
      videoTitle,
      documentId,
      published,
    }: {
      file: File;
      videoTitle: string;
      documentId: string;
      published: boolean;
    }
  ) {
    try {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowInfo}`,
        translate('started-to-upload-your-engagement-video').toString(),
        {
          root: true,
        }
      );
      const {
        video,
        uploadLink,
      }: { video: IVideo; uploadLink: string } = await VideoService.createVideo({
        videoType: EVideoSource.Upload,
        videoTitle,
        published,
      });
      await VideoService.addEngageVideo(video.id, documentId);
      await VideoService.uploadVideo({ file, uploadLink });
      await VideoService.uploadVideoDone(video.id);
      dispatch(EEngagementVideoAction.UploadVideoSuccess, video);
    } catch (error) {
      dispatch(EEngagementVideoAction.UploadVideoFailed, error);
    }
  },
};
